// 2026-07-02 US-003: simple, unfiltered, most-recent-first list of past bulk-notification
// sends (AC scope: "Simple list only" — no search/filter). Row click drills into the
// per-recipient detail for that send via GetSendBatchDetail.
import { Button, Card, Col, Modal, Row, Table, Tag, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { PATHS } from '@routing/routes';
import { Roles } from 'common/constants';
import { AccountSelector } from '@features/Account';
import { HistoryItem, SendStatus } from 'common/models/BulkNotificationTypes';
import { CheckUserAccessState, formatContainerTimestamp } from 'common/utils/utility';
import { OperationStatus } from 'store/rootTypes';
import {
  GetBulkNotificationSendBatchDetail,
  GetBulkNotificationSendHistory,
} from '../business/BulkNotificationBusiness';
import { BulkNotificationsSelector } from '../store/BulkNotificationsSelector';
import { BulkNotificationsActions } from '../store/BulkNotificationsSlice';
import '../bulkNotifications.css';

const { Text } = Typography;

const statusTagColor = (status: string): string => {
  if (status === SendStatus.Completed) return 'success';
  if (status === SendStatus.CompletedWithErrors) return 'warning';
  return 'processing';
};

export const BulkNotificationHistory = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const accountState = useSelector(AccountSelector);
  const bulkState = useSelector(BulkNotificationsSelector);

  const [selectedSendId, setSelectedSendId] = useState<string | undefined>(undefined);

  // 2026-07-02 US-003: same in-component access gate as the compose page — closes the
  // direct-navigation gap for this page too (it also exposes real customer contacts).
  useEffect(() => {
    if (accountState.RoleName !== Roles.Admin && !CheckUserAccessState(accountState.Username, accountState.RoleName)) {
      history.push(PATHS.Home.path);
    }
  }, [accountState.RoleName, accountState.Username]);

  useEffect(() => {
    dispatch(GetBulkNotificationSendHistory());
    return () => {
      dispatch(BulkNotificationsActions.SetBatchDetail(undefined));
    };
  }, []);

  const onRowClick = (record: HistoryItem) => {
    setSelectedSendId(record.SendId);
    dispatch(GetBulkNotificationSendBatchDetail(record.SendId));
  };

  const closeDetail = () => {
    setSelectedSendId(undefined);
    dispatch(BulkNotificationsActions.SetBatchDetail(undefined));
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'CreatedDate',
      key: 'CreatedDate',
      render: (value: string) => formatContainerTimestamp(value),
    },
    { title: 'Sent by', dataIndex: 'SentBy', key: 'SentBy' },
    {
      title: 'Audience',
      dataIndex: 'AudienceRef',
      key: 'AudienceRef',
      render: (value: string, record: HistoryItem) => `${record.Mode}: ${value}`,
    },
    { title: 'Channel', dataIndex: 'Channel', key: 'Channel' },
    {
      title: 'Sent / Failed',
      key: 'SentFailed',
      render: (_: unknown, record: HistoryItem) => `${record.SentCount} sent / ${record.FailedCount} failed`,
    },
    {
      title: 'Status',
      dataIndex: 'Status',
      key: 'Status',
      render: (value: string) => <Tag color={statusTagColor(value)}>{value}</Tag>,
    },
  ];

  const detailColumns = [
    { title: 'Ref no.', dataIndex: 'RefNo', key: 'RefNo' },
    { title: 'Customer', dataIndex: 'CustomerName', key: 'CustomerName' },
    { title: 'Channel', dataIndex: 'ChannelUsed', key: 'ChannelUsed' },
    { title: 'Contact', dataIndex: 'ContactValue', key: 'ContactValue' },
    {
      title: 'Status',
      dataIndex: 'Status',
      key: 'Status',
      render: (value: string) => <Tag color={value === 'Sent' ? 'success' : 'error'}>{value}</Tag>,
    },
    { title: 'Error', dataIndex: 'ErrorMessage', key: 'ErrorMessage', render: (value: string | null) => value || '—' },
  ];

  return (
    <div className="App">
      <div className="bulk-notif-page">
        <Row justify="center" align="middle">
          <h1>Sent messages</h1>
        </Row>

        <Card title="Send history" className="card-custom bulk-notif-card">
          <Table
            dataSource={bulkState.History}
            columns={columns}
            rowKey="SendId"
            size="middle"
            loading={bulkState.submitStatus === OperationStatus.pending}
            onRow={(record: HistoryItem) => ({ onClick: () => onRowClick(record) })}
            rowClassName="bulk-notif-history-row"
          />

          <Row justify="end" className="bulk-notif-actions">
            <Button size="large" onClick={() => history.push(PATHS.BulkNotifications.path)}>
              Back
            </Button>
          </Row>
        </Card>
      </div>

      <Modal
        open={!!selectedSendId}
        title="Send detail"
        onCancel={closeDetail}
        footer={null}
        width={800}
        destroyOnClose
      >
        {bulkState.BatchDetail && (
          <>
            <Row className="bulk-notif-detail-summary">
              {bulkState.BatchDetail.Subject && (
                <Col xs={24} className="bulk-notif-detail-field">
                  <Text strong>Subject</Text>
                  <div>{bulkState.BatchDetail.Subject}</div>
                </Col>
              )}
              {bulkState.BatchDetail.EmailBody && (
                <Col xs={24} className="bulk-notif-detail-field">
                  <Text strong>Email message</Text>
                  <div className="bulk-notif-detail-message">{bulkState.BatchDetail.EmailBody}</div>
                </Col>
              )}
              {bulkState.BatchDetail.SmsBody && (
                <Col xs={24} className="bulk-notif-detail-field">
                  <Text strong>SMS message</Text>
                  <div className="bulk-notif-detail-message">{bulkState.BatchDetail.SmsBody}</div>
                </Col>
              )}
              {bulkState.BatchDetail.AttachmentFileName && (
                <Col xs={24} className="bulk-notif-detail-field">
                  <Text strong>Attachment</Text>
                  <div>{bulkState.BatchDetail.AttachmentFileName}</div>
                </Col>
              )}
            </Row>
            <p className="bulk-notif-dedupe-note">
              Note: If the same email or mobile number appears more than once below, only one message was actually sent
              to it — not one per row.
            </p>
            <Table
              dataSource={bulkState.BatchDetail.Results}
              columns={detailColumns}
              rowKey={(r) => `${r.RefNo}-${r.ContactValue}`}
              size="small"
            />
          </>
        )}
      </Modal>
    </div>
  );
};
